import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PreKSettings as KSettings, DiagnosticAssessmentResult } from '../../types/kAssessmentTypes';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../hooks/useKAudio';
import { useAuth } from '../../context/AuthContext';
import { progressQueue } from '../../services/ProgressQueueService';
import { recordPageTransition, completePreKAssessment } from '../../config/api';
import { FactStatus } from '../../types/progress';
import { TRACK_RANGES } from '../../types/constants';
import WarmupTransitionScreen from './WarmupTransitionScreen';
import { AnswerChoicesGrid, AssessmentHeader } from '../PreKAssessment/shared';
import ListenButton from '../common/ListenButton';

interface NumberNamingQuestionScreenProps {
  settings: KSettings;
  onComplete: (results: DiagnosticAssessmentResult) => void;
}

const NumberNamingQuestionScreen: React.FC<NumberNamingQuestionScreenProps> = ({ settings, onComplete }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [wallClockTimeRemaining, setWallClockTimeRemaining] = useState(60000); // 60 seconds wall clock limit
  const [wallClockStarted, setWallClockStarted] = useState(false); // Flag to track when wall clock starts
  const [activeTimeElapsed, setActiveTimeElapsed] = useState(0); // Track actual active thinking time
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState(6000); // 6 seconds per question
  const [questionTimerActive, setQuestionTimerActive] = useState(false); // Per-question timer
  const [isWarmup, setIsWarmup] = useState(true);
  const [warmupCount, setWarmupCount] = useState(0);
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);
  const [currentWarmupDigit, setCurrentWarmupDigit] = useState(() => {
    const rand = Math.random();
    return rand < 1/3 ? Math.floor(Math.random() * 10) : Math.floor(Math.random() * 11) + 10;
  });
  const [currentTestDigit, setCurrentTestDigit] = useState(() => {
    const rand = Math.random();
    return rand < 1/3 ? Math.floor(Math.random() * 10) : Math.floor(Math.random() * 11) + 10;
  });
  const [waitingForCorrectAnswer, setWaitingForCorrectAnswer] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [timerActive, setTimerActive] = useState(false); // Timer pauses during TTS, transitions, waiting
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false); // Track if TTS has played for current question
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null); // Track wrong answer for red animation
  const [hasAnswered, setHasAnswered] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null); // Track selected answer for neutral feedback

  // Auth context for user ID
  const { user, updateUserField } = useAuth();

  // Start time tracking
  const testStartTime = useRef<number>(Date.now());
  const questionStartTime = useRef<number>(Date.now());
  const timerActiveStartTime = useRef<number>(0);
  const accumulatedTimerTime = useRef<number>(0);
  const questionActiveStartRef = useRef<number | null>(null);
  const questionAccumulatedTimeRef = useRef<number>(0);
  const hasCompletedRef = useRef<boolean>(false);

  // TTS integration
  const { playTTSWithCallback, isTTSPlaying, playChime } = useKTTS();

  // Get current target digit based on warmup/test state
  const targetDigit = isWarmup ? currentWarmupDigit : currentTestDigit;

  // Digit to fact mapping (digits 0-20 map to facts from K naming range)
  const digitToFact = useCallback((digit: number): string => {
    const [startFact] = TRACK_RANGES.TRACK_K_NAMING;
    return `FACT${startFact + digit}`; // Maps 0-20 to facts 1598-1618
  }, []);

  // Determine fact status based on correctness and response time (targeting 6s fluency)
  const determineFactStatus = useCallback((isCorrect: boolean, timeSpentMs: number): FactStatus => {
    if (!isCorrect) {
      return 'learning';
    }

    const timeSpentSeconds = timeSpentMs / 1000;

    if (timeSpentSeconds > 6) {
      return 'accuracyPractice';  // Need to work on accuracy first
    } else {
      return 'mastered';  // Already at target 6s fluency
    }
  }, []);

  // Wrapper to handle page transition reporting before completing
  const handleTestComplete = useCallback(async (results: DiagnosticAssessmentResult) => {
    // Report final page transition to capture total test time
    if (user?.userId) {
      try {
        await recordPageTransition({
          userId: user.userId,
          trackId: 'TRACK_K_NAMING',
          page: 'other' // Transition away from assessment
        });
      } catch (error) {
        console.error('Error recording final page transition:', error);
      }

      // Save K assessment data to database and sync focusTrack
      try {
        const result = await completePreKAssessment(user.userId, 'TRACK_K_NAMING', {
          totalQuestions: results.totalQuestions || 0,
          correctAnswers: results.correctAnswers || 0,
          activeTimeElapsed: results.activeTimeElapsed || 0
        });
        if (result.focusTrack) {
          updateUserField({ focusTrack: result.focusTrack });
        }
      } catch (error) {
        console.error('Failed to save K assessment data:', error);
      }
    }

    // Call the original onComplete
    onComplete(results);
  }, [user?.userId, onComplete, updateUserField]);

  // Generate answer choices (target digit + 3 other options)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];
    const used = new Set([target]);

    // Always include ±10 distractor if it's in valid range (0-20)
    const plusTenDistractor = target + 10;
    const minusTenDistractor = target - 10;

    if (plusTenDistractor <= 20 && !used.has(plusTenDistractor)) {
      choices.push(plusTenDistractor);
      used.add(plusTenDistractor);
    } else if (minusTenDistractor >= 0 && !used.has(minusTenDistractor)) {
      choices.push(minusTenDistractor);
      used.add(minusTenDistractor);
    }

    // Add remaining numbers to reach 4 total choices
    while (choices.length < 4) {
      const rand = Math.random();
      const candidate = rand < 1/3 ? Math.floor(Math.random() * 10) : Math.floor(Math.random() * 11) + 10;
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    // Shuffle the choices
    return choices.sort(() => Math.random() - 0.5);
  }, []);

  const [answerChoices, setAnswerChoices] = useState(() => generateAnswerChoices(targetDigit));

  // Set track ID and report page transition when component mounts
  useEffect(() => {
    // Set the active track ID for K assessment
    sessionStorage.setItem('activeTrackId', 'TRACK_K_NAMING');

    if (user?.userId) {
      recordPageTransition({
        userId: user.userId,
        trackId: 'TRACK_K_NAMING',
        page: 'assessment'
      }).catch((error: any) => {
        console.error('Error recording page transition:', error);
      });
    }
  }, [user?.userId]);

  // Update question start time when moving to test phase
  useEffect(() => {
    if (!isWarmup) {
      questionStartTime.current = Date.now();
      // Reset timer tracking for new question
      accumulatedTimerTime.current = 0;
      timerActiveStartTime.current = 0;
      // Reset per-question timing
      questionAccumulatedTimeRef.current = 0;
    }
  }, [isWarmup, currentTestDigit]);

  // Track timer active time
  useEffect(() => {
    if (isWarmup) return; // No timer tracking during warmup

    if (timerActive) {
      // Timer just became active
      timerActiveStartTime.current = Date.now();
      questionActiveStartRef.current = Date.now();
    } else {
      // Timer just became inactive - accumulate the time
      if (timerActiveStartTime.current > 0) {
        const activeTime = Date.now() - timerActiveStartTime.current;
        accumulatedTimerTime.current += activeTime;
        timerActiveStartTime.current = 0;
      }
      if (questionActiveStartRef.current) {
        const questionActiveTime = Date.now() - questionActiveStartRef.current;
        questionAccumulatedTimeRef.current += questionActiveTime;
        questionActiveStartRef.current = null;
      }
    }
  }, [timerActive, isWarmup]);

  // Wall clock timer countdown (strict 60s limit including VOs and transitions)
  useEffect(() => {
    // Don't run during warmup or before wall clock starts
    if (isWarmup || !wallClockStarted || wallClockTimeRemaining <= 0) {
      if (wallClockTimeRemaining <= 0 && wallClockStarted && user?.userId && !hasCompletedRef.current) {
        // Wall clock expired - end test immediately
        hasCompletedRef.current = true;
        handleTestComplete({
          userId: user.userId,
          testResults: [],
          recommendedStartSkill: 1,
          placementReason: 'Time limit reached (60 seconds)',
          completedAt: new Date(),
          totalQuestions,
          correctAnswers,
          activeTimeElapsed
        });
      }
      return;
    }

    const timer = setTimeout(() => {
      setWallClockTimeRemaining(prev => Math.max(0, prev - 100));
    }, 100);

    return () => clearTimeout(timer);
  }, [wallClockTimeRemaining, wallClockStarted, isWarmup, handleTestComplete, user?.userId, totalQuestions, correctAnswers, activeTimeElapsed]);

  // Active time tracking (for analytics - no limit)
  useEffect(() => {
    // Only track active time during test phase when timer is active
    if (isWarmup || !timerActive) return;

    const timer = setTimeout(() => {
      setActiveTimeElapsed(prev => prev + 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [timerActive, isWarmup, activeTimeElapsed]);

  // Play TTS when question changes
  useEffect(() => {
    // Skip TTS if already played for this question
    if (ttsHasPlayed) {
      return;
    }

    if (settings.speechEnabled) {
      const currentDigit = isWarmup ? currentWarmupDigit : currentTestDigit;

      // For K level, use K-specific audio files for digits 0-20
      const audioFile = K_QUESTION_SCREEN_AUDIO.tapNumber[currentDigit as keyof typeof K_QUESTION_SCREEN_AUDIO.tapNumber];

      // Play TTS and start timer when complete (only during test phase)
      playTTSWithCallback(audioFile, () => {
        if (!isWarmup) {
          setTimerActive(true); // Start timer only after TTS completes (test phase only)
          setQuestionTimerActive(true); // Start question timer
        }
        setTtsHasPlayed(true);
      }).catch(() => {
        // If TTS fails, still start the timer
        if (!isWarmup) {
          setTimerActive(true);
          setQuestionTimerActive(true); // Start question timer
        }
        setTtsHasPlayed(true);
      });
    } else {
      // Speech is disabled - start timer immediately
      if (!isWarmup) {
        setTimerActive(true);
        setQuestionTimerActive(true);
      }
      setTtsHasPlayed(true);
    }
  }, [isWarmup, currentWarmupDigit, currentTestDigit, warmupCount, settings.speechEnabled, playTTSWithCallback]);

  // Update answer choices and reset state when target digit changes
  useEffect(() => {
    setAnswerChoices(generateAnswerChoices(targetDigit));
    setHasAnswered(false); // Reset answer state for new question
  }, [targetDigit, generateAnswerChoices]);

  // Reset hasAnswered when waiting for correct answer
  useEffect(() => {
    if (waitingForCorrectAnswer) {
      setHasAnswered(false);
    }
  }, [waitingForCorrectAnswer]);

  // Reset TTS state when question changes
  const resetQuestionState = useCallback(() => {
    setTimerActive(false); // Pause timer during transitions
    setQuestionTimerActive(false); // Pause question timer during transitions
    setQuestionTimeRemaining(6000); // Reset question timer to 6 seconds
    setWrongAnswer(null); // Clear wrong answer animation
    setSelectedAnswer(null); // Clear selected answer for neutral feedback
  }, []);

  // Generate next random digit different from current with weighted distribution
  // 1:2 ratio = 0-9 gets 33.33%, 10-20 gets 66.67%
  const generateNextDigit = useCallback((currentDigit: number) => {
    let newDigit;
    do {
      const rand = Math.random();
      if (rand < 1/3) {
        // 33.33% chance: pick from 0-9
        newDigit = Math.floor(Math.random() * 10);
      } else {
        // 66.67% chance: pick from 10-20
        newDigit = Math.floor(Math.random() * 11) + 10;
      }
    } while (newDigit === currentDigit);
    return newDigit;
  }, []);

  // Handle warmup progression after feedback completes
  const advanceWarmupQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
    }

    const newWarmupCount = warmupCount + 1;

    // Check if warmup is complete (fixed 3 questions)
    if (newWarmupCount >= 3) {
      // Show transition screen instead of immediately starting test
      setShowTransitionScreen(true);
      return;
    }

    // Generate next random warmup digit and continue
    const newDigit = generateNextDigit(currentWarmupDigit);
    setCurrentWarmupDigit(newDigit);
    setWarmupCount(newWarmupCount);
    resetQuestionState();
  }, [waitingForCorrectAnswer, currentWarmupDigit, warmupCount, generateNextDigit, resetQuestionState]);

  // Handle transition screen completion - start the actual test
  const handleTransitionComplete = useCallback(() => {
    setShowTransitionScreen(false);
    setIsWarmup(false);
    setWarmupCount(0);
    setCurrentQuestionIndex(0);
    const newTestDigit = generateNextDigit(currentWarmupDigit);
    setCurrentTestDigit(newTestDigit);
    resetQuestionState();
    setWallClockStarted(true);
    // Reset ttsHasPlayed to allow TTS to play for first test question
    setTtsHasPlayed(false);
  }, [currentWarmupDigit, generateNextDigit, resetQuestionState]);

  // Handle test progression after feedback completes
  const advanceTestQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      // Retry after wrong answer - don't count as correct or increment totals
      setWaitingForCorrectAnswer(false);
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      const newTestDigit = generateNextDigit(currentTestDigit);
      setCurrentTestDigit(newTestDigit);
      resetQuestionState();
      return;
    }

    // First attempt correct answer - count it
    setCorrectAnswers(correctAnswers + 1);
    setTotalQuestions(totalQuestions + 1);
    const nextIndex = currentQuestionIndex + 1;
    setCurrentQuestionIndex(nextIndex);
    const newTestDigit = generateNextDigit(currentTestDigit);
    setCurrentTestDigit(newTestDigit);
    resetQuestionState();
  }, [waitingForCorrectAnswer, correctAnswers, totalQuestions, currentQuestionIndex, currentTestDigit, generateNextDigit, resetQuestionState]);

  // 6-second per-question countdown timer
  useEffect(() => {
    // Don't run question timer during warmup, when not active, or when already answered
    if (isWarmup || !questionTimerActive || questionTimeRemaining <= 0 || hasAnswered) {
      if (questionTimeRemaining <= 0 && questionTimerActive && !isWarmup && !hasAnswered) {
        // Question time expired - auto advance without showing correct answer
        if (waitingForCorrectAnswer) {
          // If waiting for correct answer, just advance
          setWaitingForCorrectAnswer(false);
          const nextIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextIndex);
          const newTestDigit = generateNextDigit(currentTestDigit);
          setCurrentTestDigit(newTestDigit);
          // Reset state but allow TTS to play for new question
          setTimerActive(false);
          setQuestionTimerActive(false);
          setQuestionTimeRemaining(6000);
          setWrongAnswer(null);
          setTtsHasPlayed(false); // Allow TTS to play for new question
          setHasAnswered(false);
        } else {
          // Count as an incorrect attempt and advance
          setTotalQuestions(totalQuestions + 1);

          // Report progress data for timeout (treat as incorrect answer)
          if (user?.userId) {
            try {
              const factId = digitToFact(currentTestDigit);
              const factStatus = determineFactStatus(false, 6000); // Use full question time as timeout response time

              progressQueue.enqueueProgressUpdate(
                user.userId,
                {
                  facts: {
                    [factId]: {
                      attempts: 1,
                      correct: 0, // Timeout is incorrect
                      timeSpent: questionAccumulatedTimeRef.current + (questionActiveStartRef.current ? Date.now() - questionActiveStartRef.current : 0), // Actual time spent on this question
                      status: factStatus
                    }
                  }
                }
              );
            } catch (error) {
              console.error('Error reporting K number naming timeout progress:', error);
            }
          }

          const nextIndex = currentQuestionIndex + 1;
          setCurrentQuestionIndex(nextIndex);
          const newTestDigit = generateNextDigit(currentTestDigit);
          setCurrentTestDigit(newTestDigit);
          // Reset state but allow TTS to play for new question
          setTimerActive(false);
          setQuestionTimerActive(false);
          setQuestionTimeRemaining(6000);
          setWrongAnswer(null);
          setTtsHasPlayed(false); // Allow TTS to play for new question
          setHasAnswered(false);
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      setQuestionTimeRemaining(prev => prev - 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [questionTimeRemaining, questionTimerActive, isWarmup, waitingForCorrectAnswer, currentQuestionIndex, currentTestDigit, totalQuestions, generateNextDigit, resetQuestionState, hasAnswered]);

  const handleAnswerClick = useCallback((selectedDigit: number) => {
    // If waiting for correct answer, only allow correct selection
    if (waitingForCorrectAnswer && selectedDigit !== targetDigit) return;

    // If not waiting and already answered, don't allow more clicks
    if (!waitingForCorrectAnswer && hasAnswered) return;

    // Force timer to be active since user has responded (bypassing TTS wait)
    if (!isWarmup) {
      setTimerActive(true);
      setQuestionTimerActive(true); // Ensure question timer is active
    }
    setTtsHasPlayed(true);
    setHasAnswered(true);
    setSelectedAnswer(selectedDigit); // Track selected answer for neutral feedback

    const currentDigit = isWarmup ? currentWarmupDigit : currentTestDigit;
    const isCorrect = selectedDigit === currentDigit;

    // Calculate per-question active response time for test questions (not warmup)
    let timerActiveResponseTime = 0;
    if (!isWarmup) {
      timerActiveResponseTime = questionAccumulatedTimeRef.current + (questionActiveStartRef.current ? Date.now() - questionActiveStartRef.current : 0);
    }

    // Report progress data for test questions (not warmup and not a retry)
    if (!isWarmup && user?.userId && !waitingForCorrectAnswer) {
      try {
        const factId = digitToFact(currentDigit);
        const factStatus = determineFactStatus(isCorrect, timerActiveResponseTime);

        // Report progress using existing queue system (like ProgressAssessment)
        progressQueue.enqueueProgressUpdate(
          user.userId,
          {
            facts: {
              [factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: timerActiveResponseTime,
                status: factStatus
              }
            }
          }
        );
      } catch (error) {
        console.error('Error reporting K progress:', error);
      }
    }

    // Handle answer logic with immediate audio feedback
    if (isWarmup) {
      if (isCorrect) {
        // Play chime and advance after a small delay
        playChime().then(() => {
          setTimeout(() => {
            setTtsHasPlayed(false);
            advanceWarmupQuestion();
          }, 300); // 300ms delay after chime completes
        });
      } else {
        // Show red animation for wrong answer
        setWrongAnswer(selectedDigit);
        setTimeout(() => setWrongAnswer(null), 1000); // Clear red animation after 1 second

        // Replay TTS instruction after boop sound if speech is enabled
        if (settings.speechEnabled) {
          const currentDigit = isWarmup ? currentWarmupDigit : currentTestDigit;
          const audioFile = K_QUESTION_SCREEN_AUDIO.tapNumber[currentDigit as keyof typeof K_QUESTION_SCREEN_AUDIO.tapNumber];

          // Play TTS after a short delay to let boop finish
          setTimeout(() => {
            playTTSWithCallback(audioFile).catch(() => {
              // Handle any TTS errors silently
            });
          }, 300); // 300ms delay after boop
        }

        setWaitingForCorrectAnswer(true);
      }
    } else {
      // Quiz mode: neutral feedback with chime for all answers, same timing
      if (!waitingForCorrectAnswer && !isCorrect) {
        setTotalQuestions(totalQuestions + 1);
      }

      playChime().then(() => {
        setTimeout(() => {
          setTtsHasPlayed(false);
          if (isCorrect) {
            advanceTestQuestion();
          } else {
            const nextIndex = currentQuestionIndex + 1;
            setCurrentQuestionIndex(nextIndex);
            const newTestDigit = generateNextDigit(currentTestDigit);
            setCurrentTestDigit(newTestDigit);
            resetQuestionState();
          }
        }, 300); // 300ms delay for all answers
      });
    }
  }, [
    isTTSPlaying, isWarmup, currentWarmupDigit, currentTestDigit, timerActive,
    user, waitingForCorrectAnswer, totalQuestions, settings.speechEnabled,
    digitToFact, determineFactStatus, playChime, playTTSWithCallback,
    advanceWarmupQuestion, advanceTestQuestion, hasAnswered, targetDigit, currentQuestionIndex, generateNextDigit, resetQuestionState
  ]);

  // Show transition screen after warmup completes
  if (showTransitionScreen) {
    return (
      <WarmupTransitionScreen
        onComplete={handleTransitionComplete}
        speechEnabled={settings.speechEnabled}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header */}
      <AssessmentHeader
        isWarmup={isWarmup}
        showTimer={true}
        wallClockTimeRemaining={wallClockTimeRemaining}
        wallClockStarted={wallClockStarted}
        isTTSPlaying={isTTSPlaying}
        timerHideable={true}
      />

      {/* Listen Button */}
      {settings.speechEnabled && (
        <div className="flex justify-center pt-4">
          <ListenButton
            onClick={() => {
              const audioFile = K_QUESTION_SCREEN_AUDIO.tapNumber[targetDigit as keyof typeof K_QUESTION_SCREEN_AUDIO.tapNumber];
              if (audioFile) playTTSWithCallback(audioFile);
            }}
            disabled={hasAnswered}
            isPlaying={isTTSPlaying}
          />
        </div>
      )}

      {/* Main Content Area - Centered Answer Choices */}
      <div className="flex-1 flex items-center justify-center px-6">
        <AnswerChoicesGrid
          choices={answerChoices}
          correctAnswer={targetDigit}
          wrongAnswer={wrongAnswer}
          hasAnswered={hasAnswered}
          waitingForCorrectAnswer={waitingForCorrectAnswer}
          disabled={false}
          onChoiceClick={handleAnswerClick}
          selectedAnswer={selectedAnswer}
          neutralFeedbackMode={!isWarmup}
        />
      </div>
    </div>
  );
};

export default NumberNamingQuestionScreen;